// src/pages/EventsPage.tsx

import React, { useEffect, useState } from 'react';
import { LegadoEvent } from '../types/Event';
import { getEvents } from '../services/eventService';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('es-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateShort = (dateStr: string): { day: string; month: string; year: string } => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return {
    day: String(day).padStart(2, '0'),
    month: date.toLocaleDateString('es-US', { month: 'short' }).toUpperCase(),
    year: String(year),
  };
};

// ─── COLORS & STYLES ─────────────────────────────────────────────────────────

const C = {
  navy: '#1B3A6B',
  navyLight: '#2A5298',
  gold: '#C8922A',
  goldLight: '#E8B84B',
  red: '#C0392B',
  green: '#27AE60',
  teal: '#2B8FC5',
  bg: '#F5F3EE',
  bgCard: '#FFFFFF',
  text: '#2C2C2C',
  textMuted: '#6B6B6B',
  border: '#E5E0D5',
};

// ─── UPCOMING EVENT CARD ─────────────────────────────────────────────────────

const UpcomingCard: React.FC<{ event: LegadoEvent }> = ({ event }) => {
  const d = formatDateShort(event.date);

  return (
    <div
      style={{
        background: C.bgCard,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(27,58,107,0.10)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        border: `1px solid ${C.border}`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(27,58,107,0.16)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(27,58,107,0.10)';
      }}
    >
      {/* Color bar top */}
      <div style={{ height: 5, background: `linear-gradient(90deg, ${C.navy}, ${C.teal})` }} />

      <div style={{ display: 'flex', flex: 1 }}>
        {/* Date block */}
        <div
          style={{
            background: C.navy,
            color: '#fff',
            minWidth: 80,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px 12px',
          }}
        >
          <span style={{ fontSize: 36, fontWeight: 800, lineHeight: 1, fontFamily: 'Georgia, serif' }}>
            {d.day}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1, marginTop: 4 }}>
            {d.month}
          </span>
          <span style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{d.year}</span>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px', flex: 1 }}>
          <div
            style={{
              display: 'inline-block',
              background: `${C.gold}22`,
              color: C.gold,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              padding: '3px 10px',
              borderRadius: 20,
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            {event.topic}
          </div>

          <h3
            style={{
              margin: '0 0 8px',
              fontSize: 18,
              fontWeight: 700,
              color: C.navy,
              fontFamily: 'Georgia, serif',
              lineHeight: 1.3,
            }}
          >
            {event.title}
          </h3>

          <p style={{ margin: '0 0 12px', fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
            {event.description}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: C.text }}>
            {event.time && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <ClockIcon /> {event.time}
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <PinIcon /> {event.location}
            </span>
          </div>
        </div>

        {/* Flyer thumbnail */}
        {event.flyerUrl && (
          <div style={{ width: 100, minHeight: 120, overflow: 'hidden', flexShrink: 0 }}>
            <img
              src={event.flyerUrl}
              alt={`Flyer: ${event.title}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── PAST EVENT CARD ──────────────────────────────────────────────────────────

const PastCard: React.FC<{ event: LegadoEvent }> = ({ event }) => {
  const [flyerOpen, setFlyerOpen] = useState(false);
  const d = formatDateShort(event.date);

  return (
    <>
      <div
        style={{
          background: C.bgCard,
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(27,58,107,0.07)',
          border: `1px solid ${C.border}`,
          display: 'flex',
          gap: 0,
          opacity: 0.92,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.opacity = '1')}
        onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.opacity = '0.92')}
      >
        {/* Left date strip */}
        <div
          style={{
            width: 64,
            background: '#E8E4DA',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 8px',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 26, fontWeight: 800, color: C.navy, fontFamily: 'Georgia, serif', lineHeight: 1 }}>
            {d.day}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.navyLight, letterSpacing: 0.8, marginTop: 3 }}>
            {d.month}
          </span>
          <span style={{ fontSize: 11, color: C.textMuted }}>{d.year}</span>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', flex: 1 }}>
          <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 4 }}>
            {event.topic}
          </div>
          <h4 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: C.navy, fontFamily: 'Georgia, serif' }}>
            {event.title}
          </h4>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
            {event.description}
          </p>
          <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 5 }}>
            <PinIcon size={12} /> {event.location}
          </div>
        </div>

        {/* Flyer button */}
        {event.flyerUrl && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              borderLeft: `1px solid ${C.border}`,
            }}
          >
            <button
              onClick={() => setFlyerOpen(true)}
              style={{
                background: 'none',
                border: `1.5px solid ${C.navy}`,
                borderRadius: 8,
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: 12,
                color: C.navy,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                whiteSpace: 'nowrap',
              }}
            >
              <ImageIcon size={14} /> Ver flyer
            </button>
          </div>
        )}
      </div>

      {/* Flyer Modal */}
      {flyerOpen && event.flyerUrl && (
        <div
          onClick={() => setFlyerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.80)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', maxWidth: 480, width: '100%' }}
          >
            <img
              src={event.flyerUrl}
              alt={`Flyer: ${event.title}`}
              style={{ width: '100%', borderRadius: 12, display: 'block', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}
            />
            <button
              onClick={() => setFlyerOpen(false)}
              style={{
                position: 'absolute',
                top: -14,
                right: -14,
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: 18,
                fontWeight: 700,
                color: C.navy,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── ICONS ────────────────────────────────────────────────────────────────────

const PinIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ImageIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<LegadoEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEvents()
      .then(setEvents)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const upcoming = events.filter((e) => e.isUpcoming).sort((a, b) => a.date.localeCompare(b.date));
  const past = events.filter((e) => !e.isUpcoming).sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* ─── HERO ─── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 60%, ${C.teal} 100%)`,
          color: '#fff',
          padding: '72px 24px 56px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        <div style={{ position: 'relative', maxWidth: 640, margin: '0 auto' }}>
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 20,
              padding: '4px 16px',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 16,
            }}
          >
            Comunidad · Aprendizaje · Crecimiento
          </div>
          <a
          href="/"
            style={{
              display: 'block',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 14,
              textDecoration: 'none',
              marginBottom: 20,
              fontWeight: 600,
            }}
          >
            ← Regresar al sitio principal
          </a>
          <h1
            style={{
              margin: 0,
              fontSize: 'clamp(32px, 6vw, 52px)',
              fontWeight: 800,
              fontFamily: 'Georgia, serif',
              lineHeight: 1.1,
              letterSpacing: -0.5,
            }}
          >
            Eventos
          </h1>
          <p style={{ margin: '16px 0 0', fontSize: 17, opacity: 0.85, lineHeight: 1.6 }}>
            Talleres, charlas y actividades comunitarias para el crecimiento profesional y personal de nuestra comunidad latina.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '48px 24px 80px' }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            Cargando eventos...
          </div>
        )}

        {!loading && (
          <>
            {/* ─── UPCOMING ─── */}
            <section style={{ marginBottom: 64 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                <span style={{ fontSize: 22 }}>📅</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: C.navy, fontFamily: 'Georgia, serif' }}>
                    Próximos Eventos
                  </h2>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: C.textMuted }}>Upcoming Events</p>
                </div>
              </div>

              {upcoming.length === 0 ? (
                <div
                  style={{
                    background: C.bgCard,
                    borderRadius: 12,
                    padding: '36px 24px',
                    textAlign: 'center',
                    border: `2px dashed ${C.border}`,
                    color: C.textMuted,
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🗓️</div>
                  <p style={{ margin: 0, fontSize: 15 }}>No hay eventos programados por el momento.</p>
                  <p style={{ margin: '6px 0 0', fontSize: 13 }}>¡Síguenos para estar al tanto de los próximos eventos!</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {upcoming.map((e) => (
                    <UpcomingCard key={e.id} event={e} />
                  ))}
                </div>
              )}
            </section>

            {/* ─── PAST ─── */}
            {past.length > 0 && (
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                  <span style={{ fontSize: 22 }}>📚</span>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: C.navy, fontFamily: 'Georgia, serif' }}>
                      Eventos Pasados
                    </h2>
                    <p style={{ margin: '2px 0 0', fontSize: 14, color: C.textMuted }}>Past Events</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {past.map((e) => (
                    <PastCard key={e.id} event={e} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
